import type { Camera } from "./camera";
import type { Text } from "../engine/things/text";
import { Strings } from "../engine/strings/strings";
import { dpr } from "./dpr";
import {
	FLOATING_TEXT_BG_COLOR,
	FLOATING_TEXT_BORDER_COLOR,
	FLOATING_TEXT_BORDER_RADIUS,
	FLOATING_TEXT_FONT_SIZE,
	FLOATING_TEXT_LINE_HEIGHT,
	FLOATING_TEXT_PADDING_X,
	FLOATING_TEXT_PADDING_Y,
} from "./floatingTextConstants";

export class FloatingTextRenderer {
	render(ctx: CanvasRenderingContext2D, camera: Camera, texts: Text[]): void {
		if (texts.length === 0) return;

		const ratio = dpr();
		const fontSize = Math.round(FLOATING_TEXT_FONT_SIZE * ratio);
		const lineHeight = Math.round(FLOATING_TEXT_LINE_HEIGHT * ratio);
		const paddingX = Math.round(FLOATING_TEXT_PADDING_X * ratio);
		const paddingY = Math.round(FLOATING_TEXT_PADDING_Y * ratio);
		const borderRadius = Math.round(FLOATING_TEXT_BORDER_RADIUS * ratio);

		ctx.save();
		ctx.font = `${fontSize}px monospace`;
		ctx.textAlign = "left";
		ctx.textBaseline = "top";

		for (const text of texts) {
			const resolved = Strings.get(text.textKey) ?? text.textKey;
			const screenPos = camera.boardToPixel(text.xy);

			// Stretch to the right edge of the viewport instead of a fixed max width.
			const availableWidth = camera.viewportWidth - screenPos.x - paddingX;
			if (availableWidth <= paddingX * 2) continue;
			const lines = wrapText(ctx, resolved, availableWidth - paddingX * 2);
			let textBlockWidth = 0;
			for (const line of lines) {
				const w = ctx.measureText(line).width;
				if (w > textBlockWidth) textBlockWidth = w;
			}

			const bgWidth = textBlockWidth + paddingX * 2;
			const bgHeight = lines.length * lineHeight + paddingY * 2;

			if (!camera.isOnScreen(screenPos.x, screenPos.y, bgWidth, bgHeight)) continue;

			// --- Draw rounded rect background ---
			ctx.fillStyle = FLOATING_TEXT_BG_COLOR;
			ctx.strokeStyle = FLOATING_TEXT_BORDER_COLOR;
			ctx.lineWidth = Math.max(1, Math.round(1 * ratio));

			ctx.beginPath();
			ctx.roundRect(screenPos.x, screenPos.y, bgWidth, bgHeight, borderRadius);
			ctx.fill();
			ctx.stroke();

			// --- Draw text lines ---
			ctx.fillStyle = text.color;
			const textStartX = screenPos.x + paddingX;
			let textY = screenPos.y + paddingY;
			for (const line of lines) {
				ctx.fillText(line, textStartX, textY);
				textY += lineHeight;
			}
		}

		ctx.restore();
	}
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
	const words = text.split(/\s+/);
	const lines: string[] = [];
	let current = "";
	for (const word of words) {
		const candidate = current.length === 0 ? word : current + " " + word;
		if (ctx.measureText(candidate).width <= maxWidth) {
			current = candidate;
		} else {
			if (current.length > 0) lines.push(current);
			// If a single word is wider than maxWidth, hard-break it.
			if (ctx.measureText(word).width > maxWidth) {
				let buffer = "";
				for (const ch of word) {
					if (ctx.measureText(buffer + ch).width > maxWidth) {
						if (buffer.length > 0) lines.push(buffer);
						buffer = ch;
					} else {
						buffer += ch;
					}
				}
				current = buffer;
			} else {
				current = word;
			}
		}
	}
	if (current.length > 0) lines.push(current);
	if (lines.length === 0) lines.push("");
	return lines;
}
